import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import type { GlobalShippingSettings } from '../../../../infrastructure/ShippingClient';

interface GlobalShippingSettingsCardProps {
  settings: GlobalShippingSettings;
  isLoading: boolean;
  isUpdating: boolean;
  error: Error | null;
  updateError: Error | null;
  onUpdate: (settings: GlobalShippingSettings) => Promise<void>;
  onRefresh: () => void;
}

const GlobalShippingSettingsCard: React.FC<GlobalShippingSettingsCardProps> = ({
  settings,
  isLoading,
  isUpdating,
  error,
  updateError,
  onUpdate,
  onRefresh,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<GlobalShippingSettings>({
    systemMessage: settings.systemMessage,
    globalShippingDelayDay: settings.globalShippingDelayDay,
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      systemMessage: settings.systemMessage,
      globalShippingDelayDay: settings.globalShippingDelayDay,
    });
  }, [settings]);

  const handleEdit = () => {
    setIsEditing(true);
    setValidationError(null);
  };

  const handleCancel = () => {
    setFormData({
      systemMessage: settings.systemMessage,
      globalShippingDelayDay: settings.globalShippingDelayDay,
    });
    setIsEditing(false);
    setValidationError(null);
  };

  const handleSave = async () => {
    if (formData.globalShippingDelayDay !== null && formData.globalShippingDelayDay < 0) {
      setValidationError('Delay days cannot be negative');
      return;
    }

    try {
      await onUpdate(formData);
      setIsEditing(false);
      setValidationError(null);
    } catch {
      // Error handled by parent
    }
  };

  const handleDelayDayChange = (value: string) => {
    const parsed = value === '' ? null : parseInt(value, 10);
    setFormData((prev) => ({
      ...prev,
      globalShippingDelayDay: isNaN(parsed as number) ? null : parsed,
    }));
    setValidationError(null);
  };

  const handleMessageChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      systemMessage: value === '' ? null : value,
    }));
  };

  if (isLoading) {
    return (
      <Card className="mb-4">
        <Card.Header className="bg-warning bg-opacity-10">
          <h5 className="mb-0">Global Shipping Settings</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 mb-0 text-muted">Loading global settings...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-4">
        <Card.Header className="bg-warning bg-opacity-10">
          <h5 className="mb-0">Global Shipping Settings</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            <Alert.Heading>Failed to load global settings</Alert.Heading>
            <p>{error.message}</p>
            <Button variant="outline-danger" size="sm" onClick={onRefresh}>
              Retry
            </Button>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header className="bg-warning bg-opacity-10 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Global Shipping Settings</h5>
        {!isEditing && (
          <Button variant="outline-primary" size="sm" onClick={handleEdit}>
            Edit
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        {updateError && (
          <Alert variant="danger" className="mb-3">
            <strong>Update failed:</strong> {updateError.message}
          </Alert>
        )}

        {validationError && (
          <Alert variant="danger" className="mb-3">
            {validationError}
          </Alert>
        )}

        {isEditing ? (
          <>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">System Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="e.g., Due to the holiday season, please expect additional delivery time."
                value={formData.systemMessage || ''}
                onChange={(e) => handleMessageChange(e.target.value)}
              />
              <Form.Text className="text-muted">
                This message will be displayed to customers on all shipping-related pages.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Global Shipping Delay (Days)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                placeholder="0"
                value={formData.globalShippingDelayDay ?? ''}
                onChange={(e) => handleDelayDayChange(e.target.value)}
                style={{ maxWidth: '150px' }}
              />
              <Form.Text className="text-muted">
                This delay will be added to all shipping duration estimates.
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="primary" onClick={handleSave} disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
              <Button variant="secondary" onClick={handleCancel} disabled={isUpdating}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className="text-muted small d-block">System Message</label>
                {settings.systemMessage ? (
                  <Alert variant="info" className="mb-0 py-2">
                    {settings.systemMessage}
                  </Alert>
                ) : (
                  <span className="text-muted fst-italic">No message set</span>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className="text-muted small d-block">Global Shipping Delay</label>
                <span className="fs-5 fw-semibold">
                  {settings.globalShippingDelayDay && settings.globalShippingDelayDay > 0
                    ? `+${settings.globalShippingDelayDay} days`
                    : 'No delay'}
                </span>
                {settings.globalShippingDelayDay && settings.globalShippingDelayDay > 0 && (
                  <div className="text-muted small">
                    Added to all shipping estimates
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default GlobalShippingSettingsCard;

